import { useTranslation } from "react-i18next";
import { Outlet, useOutletContext, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { SubNav } from "../components/SubNav";
import Approvals from "./lifecycle/Approvals";
import Attendance from "./lifecycle/Attendance";
import Roster from "./lifecycle/Roster";
import Exits from "./lifecycle/Exits";
import Gate from "./lifecycle/Gate";
import Hiring from "./lifecycle/Hiring";
import Movements from "./lifecycle/Movements";
import LifecycleHub from "./lifecycle/LifecycleHub";
import OrgChart from "./lifecycle/OrgChart";
import Payroll from "./lifecycle/Payroll";
import { usePeopleCompany } from "./People";

/** Employee lifecycle (WP-23.1, OS-0614): joining, moving and leaving, split into screens.
 *
 * This was one page of twelve stacked sections served by a single endpoint, so somebody opening the approval
 * queue waited for the org chart, six months of payroll and four weeks of timesheet lines first. Now each
 * screen fetches only what it shows. */

export const LIFECYCLE_SCREENS = ["approvals", "gate", "attendance", "hiring", "movements", "exits", "org", "payroll"] as const;

/** Mirrors `PAYROLL_ROLES` in `onesphere.api.hr`; the server is still the one that enforces it. */
const PAYROLL_ROLES = ["os_hr", "os_finance", "os_executive", "os_admin"];

export default function Lifecycle() {
  const { t } = useTranslation();
  const company = usePeopleCompany();
  const [params] = useSearchParams();
  const query = params.get("company") ? `?company=${encodeURIComponent(params.get("company")!)}` : "";
  // Payroll figures are restricted. Decided from the roles already in the token rather than by asking the server,
  // so five of the six screens do not pay for a round trip to answer one boolean. The endpoint enforces it too —
  // this only decides whether to offer the tab.
  const { identity } = useAuth();
  const canSeePayroll = (identity?.roles ?? []).some((r) => PAYROLL_ROLES.includes(r));

  const items = [
    { key: "summary", to: `/people/lifecycle${query}`, label: t("hr.tabs.summary"), end: true },
    ...LIFECYCLE_SCREENS
      .filter((k) => k !== "payroll" || canSeePayroll)
      .map((k) => ({ key: k, to: `/people/lifecycle/${k}${query}`, label: t(`hr.tabs.${k}`) })),
  ];

  return (
    <div data-lifecycle>
      <SubNav items={items} label={t("hr.sections")} />
      <Outlet context={{ company }} />
    </div>
  );
}

const useCompany = () => useOutletContext<{ company: string }>().company;

export const LifecycleIndex = () => <LifecycleHub company={useCompany()} />;
export const LifecycleApprovals = () => <Approvals company={useCompany()} />;
export const LifecycleAttendance = () => <Attendance company={useCompany()} />;
export const LifecycleGate = () => <Gate company={useCompany()} />;
export const LifecycleRoster = () => <Roster company={useCompany()} />;
export const LifecycleHiring = () => <Hiring company={useCompany()} />;
export const LifecycleMovements = () => <Movements company={useCompany()} />;
export const LifecycleExits = () => <Exits company={useCompany()} />;
export const LifecycleOrg = () => <OrgChart company={useCompany()} />;
export const LifecyclePayroll = () => <Payroll company={useCompany()} />;
